import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionBtnDots,
  DeleteModalIcon,
  FilterIcon,
  SmalDeleteIcon,
} from "../../assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { clearCredentials, setCredentials } from "../../authSlice";

function SaleList() {
  const { token } = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [disableEdit, setDisableEdit] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [saleTotalSum, setSaleTotalSum] = useState(null);
  const [filteredZimidar, setFilteredZimidar] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [deleteUserId, setDeleteUserId] = useState(undefined);
  const [allZimidars, setAllZimidars] = useState([]);
  const [saleList, setSaleList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tokenExpired, setTokenExpired] = useState(false);

  const toggleDropdown = (index) => {
    setOpenRowIndex(openRowIndex === index ? null : index);
  };

  const handleDeleteUser = (id) => {
    setShowModal(true);
    setDeleteUserId(id);
  };

  const handleDateChange = (e) => {
    const value = e.target.value;

    // Allow only digits and dashes, max length 10 (DD-MM-YYYY)
    if (/^[0-9-]*$/.test(value) && value.length <= 10) {
      setSelectedDate(value);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filteredZimidar) queryParams.append("zimindarId", filteredZimidar);
      if (selectedDate) queryParams.append("date", selectedDate);

      const apiUrl = `${process.env.REACT_APP_API_URL}/sales/getSalesByDateAndZimindar?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        dispatch(clearCredentials());
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const data = await response.json();
        setDisableEdit(false);
        setSaleList(data.sales);
        setSaleTotalSum(data.totalSum);
        console.log("Sales data fetched successfully");
      } else {
        const data = await response.json();
        toast.error(data.message, {
          style: {
            border: "1px solid #FE0000",
            padding: "16px",
            color: "#FE0000",
          },
          iconTheme: {
            primary: "#FE0000",
            secondary: "#FE000099",
          },
          duration: 6000,
        });
        console.error("Failed to fetch sales data:", data);
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClear = () => {
    setSelectedDate("");
    setFilteredZimidar("");
    fetchSales();
  };

  const handleEditUserLink = (id) => {
    navigate(`/dashboard/edit-sale/${id}`);
  };

  const fetchSales = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sales/getSalesDataToday`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setTokenExpired();
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const saleData = await response.json();
        // console.log(saleData, saleData.sales.length);

        setSaleList(saleData.sales);
        setDisableEdit(false);
        setSaleTotalSum(saleData.totalSum);
      } else {
        const data = await response.json();
        toast.error(data.message, {
          style: {
            border: "1px solid #FE0000",
            padding: "16px",
            color: "#FE0000",
          },
          iconTheme: {
            primary: "#FE0000",
            secondary: "#FE000099",
          },
          duration: 6000,
        });
        setError("Failed to load sales");
      }
    } catch (err) {
      setError("Error fetching sales");
    } finally {
      setLoading(false);
    }
  }, [dispatch, token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenRowIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchZimidars = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getAllZimindars`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const data = await response.json();
          setAllZimidars(data.zimindars || []);
        } else {
          const data = await response.json();
          toast.error(data.message, {
            style: {
              border: "1px solid #FE0000",
              padding: "16px",
              color: "#FE0000",
            },
            iconTheme: {
              primary: "#FE0000",
              secondary: "#fff",
            },
          });
          throw new Error("Failed to fetch zimidars.");
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    if (tokenExpired) {
      dispatch(clearCredentials());
      return;
    }

    if (!token || token === "null") {
      dispatch(setCredentials({ token: null, username: null }));
      navigate("/login");
      setError("No token available. Please log in.");
      setLoading(false);
    } else {
      fetchZimidars();
      fetchSales();
    }
  }, [navigate, token, fetchSales, dispatch, tokenExpired]);

  const formatDate = (dateString) => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-UK", options);
  };

  const onConfirmDelete = async () => {
    if (!deleteUserId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sales/delete?id=${deleteUserId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        dispatch(clearCredentials());
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        toast("Payment deleted successfully!", {
          icon: <SmalDeleteIcon />,
        });
        fetchSales();
      } else {
        const data = await response.json();
        toast.error(data.message, {
          style: {
            border: "1px solid #FE0000",
            padding: "16px",
            color: "#FE0000",
          },
          iconTheme: {
            primary: "#FE0000",
            secondary: "#FE000099",
          },
          duration: 6000,
        });
        console.error("Failed to delete sale:", data);
      }
    } catch (error) {
      console.error("Error deleting sale:", error);
    } finally {
      setLoading(false);
      setShowModal(false);
      setDeleteUserId(undefined);
    }
  };

  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-2">
        <div className="mainHeading">Sales List </div>
        <div className="d-flex gap-3">
          {showFilter ? (
            <button className="grayBtn" onClick={() => setShowFilter(false)}>
              Close
            </button>
          ) : (
            <button
              className="colorBtn addNewBtn"
              onClick={() => setShowFilter(true)}
            >
              <FilterIcon />
              <span>Filter</span>
            </button>
          )}
        </div>
      </div>
      <div className={`filterDropDown ${showFilter && "show"}`}>
        <div className="filterFlex">
          <div className="formInputLabelBox">
            <label>Choose Date</label>
            <input
              name="chooseDate"
              placeholder="Choose Date"
              type="date"
              id="date"
              value={selectedDate}
              onChange={handleDateChange}
            />
          </div>
          <div className="formInputLabelBox">
            <label>Choose Zimidar</label>
            <select
              name="zimindarId"
              className="form-select"
              value={filteredZimidar}
              onChange={(e) => setFilteredZimidar(e.target.value)}
            >
              <option value="">Select Zimindar</option>
              {allZimidars.map((zimidar) => (
                <option key={zimidar._id} value={zimidar._id}>
                  {zimidar.name}
                </option>
              ))}
            </select>
          </div>
          {!loading ? (
            <button className="colorBtn" onClick={handleApply}>
              Apply
            </button>
          ) : (
            <button className="colorBtn">Loading ...</button>
          )}
          <button className="grayBtn" onClick={handleFilterClear}>
            Clear
          </button>
        </div>
      </div>
      <div className="tableDiv" style={{ paddingBottom: "69px" }}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Date</th>
              <th scope="col">Zimidar</th>
              <th scope="col">Customer</th>
              <th scope="col">Item</th>
              <th scope="col">Price</th>
              <th scope="col">Weight</th>
              <th scope="col">Total</th>
              <th scope="col">Grand Total</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          {!loading ? (
            <tbody>
              {saleList && saleList.length > 0 ? (
                saleList.map((sale, i) => {
                  const isLast = saleList.length - 1 <= i;
                  const isTotalDiv = saleList.length - 1 <= i;
                  const roundUp = (value) =>
                    Number.isInteger(value) ? value : Math.ceil(value);
                  return (
                    <tr
                      key={sale._id}
                      className={`${sale?.netPrice && "net_price"}`}
                    >
                      <th scope="row">{i + 1}</th>
                      <td>{formatDate(sale.date)}</td>
                      <td>{sale.zimindarId.name}</td>
                      <td>{sale.customerId.name}</td>
                      <td>{sale.item}</td>
                      <td>{roundUp(sale.price)}</td>
                      <td>{sale.weight}kg</td>
                      <td>
                        {isTotalDiv ? (
                          <div className="position-relative">
                            {roundUp(sale.total)}
                            <div className="totalvaluePositionAbsolute">
                              Total: {roundUp(saleTotalSum)}
                            </div>
                          </div>
                        ) : (
                          roundUp(sale.total)
                        )}
                      </td>
                      <td>{roundUp(sale.grandTotal)}</td>
                      <td>
                      <div className="actionDropDown">
                            <span onClick={() => toggleDropdown(i)}>
                              <ActionBtnDots />
                            </span>
                            {openRowIndex === i && (
                              <div
                                className={`dropDownDiv 
                             ${isLast ? "bottom100" : ""}
                            `}
                                ref={dropdownRef}
                              >
                                <button
                                  className="delBtn"
                                  onClick={() => handleDeleteUser(sale._id)}
                                >
                                  Delete
                                </button>
                                <button
                                  className="editBtn"
                                  onClick={() => handleEditUserLink(sale._id)}
                                >
                                  <span>Edit</span>
                                </button>
                              </div>
                            )}
                          </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10}>No entries Found</td>
                </tr>
              )}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan={6}>Loading ...</td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {showModal && (
        <div className="modalBackdrop">
          <div className="modalContent">
            <div className="delIcon">
              <DeleteModalIcon />{" "}
            </div>
            <p>Are you sure you want to delete this user?</p>
            <div className="modalActions">
              {!loading ? (
                <button className="colorBtn" onClick={onConfirmDelete}>
                  Delete
                </button>
              ) : (
                <button className="colorBtn">Deleting ...</button>
              )}
              <button className="grayBtn" onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SaleList;
